import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IProgressIndicatorStepProps, PROGRESS_INDICATOR_STATUS, ProgressIndicatorStatus, ProgressIndicatorStep } from './ProgressIndicatorStep';
import { ColourAssignment, IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { IProgressIndicatorBaseProps, ProgressIndicatorContext } from './ProgressIndicatorContext';
import { useResponsive } from '../../hooks/useResponsive/useResponsive';
import { ProgressIndicatorStepMobile } from './ProgressIndicatorStepMobile';

export const progressIndicatorColours = ({ palette }: IColoursCollection): { progressIndicatorColours: Record<ProgressIndicatorStatus, ColourAssignment> } => ({
  progressIndicatorColours: {
    [PROGRESS_INDICATOR_STATUS.COMPLETED]: {
      text: palette.success,
      border: `${PX_TO_REM[2]} solid ${palette.success}`
    },
    [PROGRESS_INDICATOR_STATUS.CURRENT]: {
      text: palette.info,
      border: `${PX_TO_REM[2]} solid ${palette.info}`
    },
    [PROGRESS_INDICATOR_STATUS.PENDING]: {
      text: palette.neutral300,
      border: `${PX_TO_REM[2]} solid ${palette.neutral300}`
    }
  }
});

export interface IProgressIndicatorProps extends IProgressIndicatorBaseProps {
  steps: IProgressIndicatorStepProps[];
}

const ProgressIndicatorContainer = styled(GapContainer)``;
const MAX_STEPS_ON_TABLET = 4;

export const getButtonText: IProgressIndicatorBaseProps['t'] = (current, length) => `${current} of ${length}`;

export const ProgressIndicator: React.FC<IProgressIndicatorProps> = ({
  steps,
  isIconSmall,
  hidePosition,
  showStatusIcon,
  sheetTitle,
  t = getButtonText,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'ProgressIndicator');
  const { size, ref, isTablet, isMobile } = useResponsive();
  const length = steps.at(-1)?.position ?? steps.length;
  const isCompact = isTablet || isMobile;
  const contextProps: IProgressIndicatorBaseProps = {
    isIconSmall,
    showStatusIcon,
    hidePosition,
    sheetTitle,
    t,
    length,
    isCompact
  };

  const isTableWithMobileView = size === SIZES.MEDIUM && steps?.length > MAX_STEPS_ON_TABLET;
  const showMobileView = isMobile || isTableWithMobileView;
  const [transformedSteps, setTransformedSteps] = useState<IProgressIndicatorProps['steps']>(steps);
  const [current, setCurrent] = useState(steps.find((step) => step.status === 'current') ?? steps[0]);

  useEffect(() => {
    const modifiedSteps = steps.map((step, index) => ({
      ...step,
      position: step.position ?? index + 1,
      icon: isTableWithMobileView ? undefined : step.icon
    }));
    setTransformedSteps(modifiedSteps);
    setCurrent(modifiedSteps.find((step) => step.status === 'current') ?? modifiedSteps[0]);
  }, [steps, size, isMobile]);

  return (
    <ProgressIndicatorContext.Provider value={contextProps}>
      <ProgressIndicatorContainer ref={ref} gap={16} {...generateTestId()} {...rest}>
        {showMobileView ? (
          <ProgressIndicatorStepMobile steps={transformedSteps} {...current} />
        ) : (
          transformedSteps.map((step) => <ProgressIndicatorStep key={step.title} {...step} />)
        )}
      </ProgressIndicatorContainer>
    </ProgressIndicatorContext.Provider>
  );
};
